import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  BankInstitutionDownload,
  BankInstitutionDto,
  BankInstitutionFilterSelects,
  BankInstitutionFilterStatus,
  BankInstitutionListQuery,
  BankInstitutionListResult,
  BankInstitutionRequest,
  BankInstitutionStatusDto,
} from './bank-institutions-api.types';
import { ErpApiError, mapErpError } from './erp-api.errors';
import { ERP_API_URL } from './erp-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const INSTITUTIONS_PATH = '/treasury/catalogs/bank-institutions';

@Injectable({ providedIn: 'root' })
export class BankInstitutionsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ERP_API_URL).replace(/\/+$/, '')}${INSTITUTIONS_PATH}`;

  list(query: BankInstitutionListQuery = {}): Observable<BankInstitutionListResult> {
    return this.http.get<unknown>(this.baseUrl, { params: listParams(query) }).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(body => unwrapList(body)),
      catchError(error => throwError(() => mapBankError(error))),
    );
  }

  filterSelects(): Promise<BankInstitutionFilterSelects> {
    return this.read(
      this.http
        .get<unknown>(`${this.baseUrl}/filter-selects`)
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      unwrapFilterSelects,
    );
  }

  getById(uuid: string): Promise<BankInstitutionDto> {
    return this.read(
      this.http
        .get<unknown>(`${this.baseUrl}/${uuid}`)
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      unwrapInstitution,
    );
  }

  create(payload: BankInstitutionRequest): Promise<BankInstitutionDto> {
    return this.mutate(this.http.post<unknown>(this.baseUrl, payload), unwrapInstitution);
  }

  update(uuid: string, payload: BankInstitutionRequest): Promise<BankInstitutionDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapInstitution,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  exportExcel(query: BankInstitutionListQuery = {}): Promise<BankInstitutionDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      listParams(query, false),
      'instituciones-bancarias.xlsx',
    );
  }

  private async read<T>(source: Observable<unknown>, project: (body: unknown) => T): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapBankError(error);
    }
  }

  private async mutate<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    return this.read(source, project);
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<BankInstitutionDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, { params, observe: 'response', responseType: 'blob' })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapBankError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) {
      return timer(300);
    }
  }
  return throwError(() => error);
}

function listParams(
  query: BankInstitutionListQuery,
  includePagination = true,
): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.shortName) params = params.set('filters[shortName]', query.shortName);
  if (query.legalName) params = params.set('filters[legalName]', query.legalName);
  if (query.statusUuids?.length) {
    params = params.set('select[status]', query.statusUuids.join(','));
  }
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function mapBankError(error: unknown): ErpApiError {
  const mapped = mapErpError(error);
  if (!(error instanceof HttpErrorResponse)) return mapped;
  const body = asRecord(error.error);
  if (!body) return mapped;
  const business = readString(body, 'error');
  const details = detailMessages(body['details']).filter(item => item !== business);
  if (business && details.length) {
    mapped.message = [business.replace(/\.$/, ''), ...details].join('. ');
  } else if (business) {
    mapped.message = business;
  } else if (details.length) {
    mapped.message = details.join('. ');
  }
  return mapped;
}

function unwrapList(body: unknown): BankInstitutionListResult {
  const page = asRecord(unwrapData(body));
  const nested = page?.['data'];
  if (!Array.isArray(nested)) throw new ErpApiError('unexpected', 0);
  const items = nested.map(item => {
    const institution = normalizeInstitution(item);
    if (!institution) throw new ErpApiError('unexpected', 0);
    return institution;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return {
    items,
    total: typeof totalRecords === 'number' ? totalRecords : items.length,
  };
}

function unwrapInstitution(body: unknown): BankInstitutionDto {
  const institution = normalizeInstitution(unwrapData(body));
  if (!institution) throw new ErpApiError('unexpected', 0);
  return institution;
}

function unwrapFilterSelects(body: unknown): BankInstitutionFilterSelects {
  const data = asRecord(unwrapData(body)) ?? {};
  const statuses = Array.isArray(data['status']) ? data['status'] : [];
  return {
    statuses: statuses
      .map(normalizeFilterStatus)
      .filter((item): item is BankInstitutionFilterStatus => item !== null),
  };
}

function normalizeInstitution(raw: unknown): BankInstitutionDto | null {
  const record = asRecord(raw);
  const uuid = readString(record, 'uuid');
  const status = normalizeStatus(record?.['status']);
  if (!record || !uuid || !status) return null;
  return {
    uuid,
    shortName: readString(record, 'shortName'),
    legalName: readString(record, 'legalName'),
    status,
    createdAt: optionalString(record, 'createdAt'),
    updatedAt: optionalString(record, 'updatedAt'),
  };
}

function normalizeStatus(raw: unknown): BankInstitutionStatusDto | null {
  const record = asRecord(raw);
  const name = readString(record, 'name');
  const value = readString(record, 'value');
  if (!name || !value) return null;
  return { name, value, tone: readString(record, 'tone') };
}

function normalizeFilterStatus(raw: unknown): BankInstitutionFilterStatus | null {
  const record = asRecord(raw);
  const uuid = readString(record, 'uuid');
  const name = readString(record, 'name');
  if (!uuid || !name) return null;
  return { uuid, name };
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): BankInstitutionDownload {
  if (!response.body) throw new ErpApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function detailMessages(details: unknown): string[] {
  const record = asRecord(details);
  if (!record) return [];
  const messages: string[] = [];
  for (const value of Object.values(record)) {
    if (typeof value === 'string' && value.trim()) messages.push(value.trim());
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) messages.push(item.trim());
    }
  }
  return messages;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(
  record: Record<string, unknown> | null,
  key: string,
): string {
  const value = record?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function optionalString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = readString(record, key);
  return value || undefined;
}
